import { useMemo } from 'react'
import * as THREE from 'three'
import { ancorasPecasRigidas } from '../../dominio/didatico/campoDeformacao'
import { xDaEstruturaNaAltura, type PerfilVeiculo } from '../../dominio/didatico/perfilVeiculo'
import { useDeformacao } from './contextoDeformacao'

/**
 * Trincas dos vidros atingidos.
 *
 * O estado de cada vidro (íntegro, trincado, rompido) vem do domínio
 * (`estadoDoVidro`). Trincado desenha um padrão de linhas radiais e anéis
 * sobre o vidro; rompido é tratado pela própria peça, que deixa de ser
 * desenhada. Não há simulação de fragmentos — é leitura visual.
 */

/** Padrão determinístico de trincas no plano XY local, raio 1. */
export function padraoTrincas(semente: number, raios = 9): Float32Array {
  const segmentos: number[] = []
  const aleatorio = (i: number) => {
    const x = Math.sin(semente * 12.9898 + i * 78.233) * 43758.5453
    return x - Math.floor(x)
  }
  const angulos: number[] = []
  for (let i = 0; i < raios; i += 1) {
    const angulo = ((i + aleatorio(i) * 0.6) / raios) * Math.PI * 2
    angulos.push(angulo)
    // cada raio é uma polilinha quebrada, não uma reta
    let r0 = 0
    let a0 = angulo
    const passos = 4
    for (let k = 1; k <= passos; k += 1) {
      const r1 = (k / passos) * (0.65 + aleatorio(i + k * 7) * 0.35)
      const a1 = angulo + (aleatorio(i * 3 + k) - 0.5) * 0.25
      segmentos.push(Math.cos(a0) * r0, Math.sin(a0) * r0, 0, Math.cos(a1) * r1, Math.sin(a1) * r1, 0)
      r0 = r1
      a0 = a1
    }
  }
  // anéis concêntricos incompletos ligando raios vizinhos
  for (const anel of [0.28, 0.55]) {
    for (let i = 0; i < angulos.length; i += 1) {
      if (aleatorio(i * 11 + anel * 100) < 0.35) continue
      const a = angulos[i]!
      const b = angulos[(i + 1) % angulos.length]! + (i + 1 === angulos.length ? Math.PI * 2 : 0)
      const r = anel * (0.9 + aleatorio(i + 50) * 0.2)
      segmentos.push(Math.cos(a) * r, Math.sin(a) * r, 0, Math.cos(b) * r, Math.sin(b) * r, 0)
    }
  }
  return new Float32Array(segmentos)
}

interface VidroComTrinca {
  id: string
  posicao: [number, number, number]
  normal: [number, number, number]
  raio: number
}

function vidrosDoPerfil(perfil: PerfilVeiculo): VidroComTrinca[] {
  const ancoras = ancorasPecasRigidas(perfil)
  const lista: VidroComTrinca[] = []
  const paraBrisa = ancoras.paraBrisa!
  const dyA = perfil.tetoY - perfil.cinturaY
  const dzA = perfil.colunaA.zTopo - perfil.colunaA.zBase
  const nA = new THREE.Vector3(0, -dzA, dyA).normalize()
  lista.push({ id: 'paraBrisa', posicao: [0, paraBrisa.y, paraBrisa.z + 0.02], normal: [nA.x, nA.y, nA.z], raio: 0.22 })
  const traseiro = ancoras.vidroTraseiro!
  const dzC = perfil.colunaC.zTopo - perfil.colunaC.zBase
  const nC = new THREE.Vector3(0, dzC, -dyA).normalize()
  lista.push({ id: 'vidroTraseiro', posicao: [0, traseiro.y, traseiro.z - 0.02], normal: [nC.x, nC.y, nC.z], raio: 0.24 })
  for (const [sinal, sufixo] of [
    [1, 'Esquerda'],
    [-1, 'Direita'],
  ] as const) {
    for (const porta of ['Dianteiro', 'Traseiro'] as const) {
      const id = `vidro${porta}${sufixo}`
      const a = ancoras[id]!
      const x = xDaEstruturaNaAltura(perfil, sinal, a.y) + sinal * 0.05
      lista.push({ id, posicao: [x, a.y, a.z], normal: [sinal, 0, 0], raio: 0.16 })
    }
  }
  return lista
}

export function DanoVidros({ perfil }: { perfil: PerfilVeiculo }) {
  const ctx = useDeformacao()
  const vidros = useMemo(() => vidrosDoPerfil(perfil), [perfil])
  const geometria = useMemo(() => {
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.BufferAttribute(padraoTrincas(3), 3))
    return g
  }, [])
  if (!ctx) return null
  const trincados = vidros.filter((v) => ctx.pecas[v.id]?.vidro === 'trincado')
  if (trincados.length === 0) return null
  return (
    <group>
      {trincados.map((v, i) => {
        const q = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), new THREE.Vector3(...v.normal))
        return (
          <lineSegments
            key={v.id}
            geometry={geometria}
            position={v.posicao}
            quaternion={q}
            scale={[v.raio, v.raio * (0.85 + (i % 3) * 0.08), 1]}
            renderOrder={6}
          >
            <lineBasicMaterial color="#e2e8f0" transparent opacity={0.85} depthWrite={false} />
          </lineSegments>
        )
      })}
    </group>
  )
}
